/**
 * SDX React mount adapter.
 *
 * Implements the FrameworkMountAdapter contract from
 * @sdx/core/mount so the framework-agnostic core pipeline can
 * drive React mounts. Every piece of code that is identical
 * across SDX framework modules (behavior installer, lazy
 * observer, props reader, component resolver, crash reporter,
 * mount coordinator) lives in the core; this file only
 * supplies the React-specific bits:
 *
 * - React Refresh preamble injection in dev mode
 *   (vite-preamble.ts)
 * - React + ReactDOM dynamic import (react-loader.ts)
 * - createRoot / hydrateRoot mount call wrapped in the SDX
 *   error boundary (error-boundary.ts)
 * - SDX Reactive rerender hook (uses the boundary too)
 *
 * The adapter is a single value, not a class, because it has
 * no state of its own - every state container (mount registry,
 * loaded component map, vite client promise) is owned by core
 * or by the loader/preamble helpers' module-level memoization.
 */

import type {
  FrameworkMountAdapter,
  FrameworkSettings,
  MountParams,
  MountRegistries,
} from '@sdx/core/mount';
import { loadReact, unwrapModule } from './react-loader';
import { ensureVitePreamble } from './vite-preamble';
import { getErrorBoundary } from './error-boundary';
import { readReactSettings } from './types';
import type { ReactDomLike, ReactLike, ReactRoot } from './types';

type ReactPair = [ReactLike, ReactDomLike];

/**
 * Vite client lives inside the preamble loader for React: the
 * @vite/client script is wired up by `installPreamble`, so the
 * adapter exposes a stub `ensureDevClient` that just defers to
 * `ensurePreamble`. Splitting the methods at the contract level
 * keeps Vue and Svelte (which need only the client, not a
 * preamble) able to express the simpler case.
 */
export const reactMountAdapter: FrameworkMountAdapter<ReactRoot, ReactPair> = {
  behaviorName: 'sdxReactMount',
  logPrefix: '[SDX React]',

  readSettings(): FrameworkSettings {
    return readReactSettings();
  },

  ensurePreamble(devServer: string | null): Promise<void> {
    return ensureVitePreamble(devServer);
  },

  ensureDevClient(_devServer: string | null): Promise<void> {
    // The React Refresh preamble installer also injects
    // @vite/client, so there is nothing extra to do here.
    return Promise.resolve();
  },

  loadRuntime(devServer: string | null): Promise<ReactPair> {
    return loadReact(devServer);
  },

  mount(params: MountParams<ReactPair>): ReactRoot {
    const [reactModule, reactDomModule] = params.runtime;
    const React = unwrapModule(reactModule);
    const ReactDOM = unwrapModule(reactDomModule);

    const componentId = params.container.dataset.component ?? '';
    const Boundary = getErrorBoundary(React);
    const wrapped = React.createElement(
      Boundary,
      { componentId },
      React.createElement(params.component as never, params.props as never),
    );

    if (params.hydrate) {
      // Without this hook a hydration mismatch surfaces as an opaque
      // uncaught minified React error; the component stack is the
      // only clue to WHICH element mismatched on a production build.
      return ReactDOM.hydrateRoot(params.container, wrapped, {
        onRecoverableError: (error: unknown, errorInfo: { componentStack?: string }) => {
          console.error(
            `${reactMountAdapter.logPrefix} Recoverable hydration error for ${componentId}:`,
            error instanceof Error ? error.message : error,
            errorInfo?.componentStack ?? '',
          );
        },
      });
    }
    const root = ReactDOM.createRoot(params.container);
    root.render(wrapped);
    return root;
  },

  unmount(instance: ReactRoot): void {
    instance.unmount();
  },

  registerRerenderHook(runtime: ReactPair, registries: MountRegistries<ReactRoot>): void {
    if (typeof Drupal === 'undefined') return;
    const sdx = Drupal.sdx ?? {};
    if (sdx.rerender) return;

    const [reactModule] = runtime;
    const React = unwrapModule(reactModule);

    sdx.rerender = (container: Element, props: unknown) => {
      const root = registries.mountedRoots.get(container);
      const compId = (container as HTMLElement).dataset.component;
      if (!root || !compId) return;
      const Component = registries.loadedComponents.get(compId);
      if (!Component) return;
      const Boundary = getErrorBoundary(React);
      root.render(
        React.createElement(
          Boundary,
          { componentId: compId },
          React.createElement(Component as never, props as never),
        ),
      );
    };
    Drupal.sdx = sdx;
  },
};
